import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import PageRichText from "./PageRichText";
import { usePageTextBlock } from "./usePageTextBlock";
import { cn } from "../lib/utils";

interface Props {
  element: DocumentElementType;
}

/**
 * A to-do (checklist) item: a toggleable checkbox + rich text. `data.checked`
 * holds the tick; when set the text is struck through and muted. One element per
 * item, so Enter continues the checklist (`next_element` → itself) and an empty
 * item exits to text.
 */
const PageTodoList: React.FC<Props> = ({ element }) => {
  const data = element.data as { content?: string; checked?: boolean };
  const checked = data?.checked === true;
  const { richTextProps, update } = usePageTextBlock(element);

  return (
    <div className="flex gap-[11px]">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label="Toggle to-do"
        disabled={!update}
        // preventDefault so clicking the box doesn't blur/steal from the editor.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          update?.((d) => {
            d.checked = !checked;
          })
        }
        className={cn(
          "mt-[3px] flex h-[17px] w-[17px] flex-shrink-0 items-center justify-center rounded-[5px] border-[1.6px] transition-colors",
          checked
            ? "border-[#2f6fdb] bg-[#2f6fdb] text-white"
            : "border-[#cbc7bf] bg-white hover:border-[#a5a199]",
          update ? "cursor-pointer" : "cursor-default",
        )}
      >
        {checked && <FontAwesomeIcon icon={faCheck} className="h-[9px] w-[9px]" />}
      </button>
      <div className="min-w-0 flex-1">
        <PageRichText
          {...richTextProps}
          className={cn(checked && "line-through opacity-50")}
        />
      </div>
    </div>
  );
};

export default PageTodoList;
